import { useState, useRef, useEffect } from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { useVideoUpload } from "@/hooks/useVideoUpload";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Video,
  Upload,
  Trash2,
  RefreshCw,
  Image,
  Play,
  Pause,
  Heart,
  Globe,
  Building2,
  Loader2,
} from "lucide-react";

const ProfileVideoSection = () => {
  const { profile } = useAuthContext();
  const { uploadVideo, deleteVideo, updateVideoSettings, uploading, progress } = useVideoUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [coverTimestamp, setCoverTimestamp] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  // Visibility toggles - use type assertion for new fields
  const profileAny = profile as any;
  const [showPopBalloon, setShowPopBalloon] = useState(profileAny?.video_show_pop_balloon ?? true);
  const [showGlobalConnect, setShowGlobalConnect] = useState(profileAny?.video_show_global_connect ?? true);
  const [showExhibition, setShowExhibition] = useState(profileAny?.video_show_exhibition ?? true);

  // Sync with profile
  useEffect(() => {
    if (profile) {
      const p = profile as any;
      setShowPopBalloon(p.video_show_pop_balloon ?? true);
      setShowGlobalConnect(p.video_show_global_connect ?? true);
      setShowExhibition(p.video_show_exhibition ?? true);
      setCoverTimestamp(p.video_cover_timestamp ?? 0);
    }
  }, [profile]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check video duration (max 15 seconds)
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = async () => {
      window.URL.revokeObjectURL(video.src);
      
      if (video.duration > 15) {
        toast({
          title: "Video too long",
          description: "Please upload a video that is 15 seconds or less",
          variant: "destructive",
        });
        return;
      }

      const url = await uploadVideo(file);
      if (url) {
        toast({ title: "Video uploaded!", description: "Your intro video is now live" });
      }
    };

    video.onerror = () => {
      toast({
        title: "Invalid video",
        description: "Could not read video file",
        variant: "destructive",
      });
    };

    video.src = URL.createObjectURL(file);

    // Reset input
    e.target.value = "";
  };

  const handleDelete = async () => {
    const success = await deleteVideo();
    if (success) {
      toast({ title: "Video deleted" });
    }
    setShowDeleteConfirm(false);
  };

  const handleVisibilityChange = async (
    key: "video_show_pop_balloon" | "video_show_global_connect" | "video_show_exhibition",
    value: boolean
  ) => {
    // Update local state immediately
    if (key === "video_show_pop_balloon") setShowPopBalloon(value);
    if (key === "video_show_global_connect") setShowGlobalConnect(value);
    if (key === "video_show_exhibition") setShowExhibition(value);

    // Save to database
    await updateVideoSettings({ [key]: value });
  };

  const handleVideoLoaded = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
      // Set initial time to cover timestamp
      videoRef.current.currentTime = coverTimestamp;
    }
  };

  const togglePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSetCoverFrame = async () => {
    if (!videoRef.current) return;

    const timestamp = videoRef.current.currentTime;
    setCoverTimestamp(timestamp);

    const success = await updateVideoSettings({ video_cover_timestamp: timestamp });
    if (success) {
      toast({ title: "Cover frame saved!" });
    }
    setShowCoverPicker(false);
  };

  const hasVideo = profile?.has_intro_video && profile?.video_url;

  return (
    <div className="bg-card rounded-2xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Video className="w-5 h-5 text-primary" />
          Profile Video
        </h3>
        {hasVideo && (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
            Max 15 seconds
          </span>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload progress */}
      {uploading && progress && (
        <div className="space-y-2 p-4 bg-muted/50 rounded-xl">
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Uploading video...</span>
            <span className="ml-auto font-medium">{Math.round(progress.percentage)}%</span>
          </div>
          <Progress value={progress.percentage} className="h-2" />
        </div>
      )}

      {!hasVideo ? (
        /* No video - Show upload CTA */
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full p-6 border-2 border-dashed border-border rounded-xl flex flex-col items-center gap-3 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Upload className="w-8 h-8 text-primary" />
          </div>
          <div className="text-center">
            <p className="font-semibold">Add Intro Video</p>
            <p className="text-sm text-muted-foreground">
              Record a short intro (max 15 seconds)
            </p>
          </div>
        </button>
      ) : (
        /* Has video - Show preview and actions */
        <div className="space-y-4">
          {/* User info + Video preview */}
          <div className="flex items-start gap-3">
            {/* User avatar and name */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
                {profile?.photo_url ? (
                  <img
                    src={profile.photo_url}
                    alt={profile.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xl font-bold text-muted-foreground">
                    {profile?.name?.[0] || "?"}
                  </div>
                )}
              </div>
            </div>

            {/* Video preview */}
            <div className="flex-1 relative rounded-xl overflow-hidden bg-black aspect-video">
              <video
                ref={videoRef}
                src={profile.video_url}
                className="w-full h-full object-cover"
                onLoadedMetadata={handleVideoLoaded}
                onEnded={() => setIsPlaying(false)}
                playsInline
                muted={!isPlaying}
              />

              {/* Play/pause overlay */}
              <button
                onClick={togglePlayPause}
                className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-foreground" />
                  ) : (
                    <Play className="w-5 h-5 text-foreground ml-0.5" />
                  )}
                </div>
              </button>

              {/* Duration badge */}
              {videoDuration > 0 && (
                <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs">
                  {Math.round(videoDuration)}s
                </div>
              )}
            </div>
          </div>

          {/* User name display */}
          <p className="text-sm font-medium">{profile?.name}'s Intro Video</p>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Replace
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCoverPicker(true)}
            >
              <Image className="w-4 h-4 mr-2" />
              Cover Frame
            </Button>
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Video
          </Button>

          {/* Visibility toggles */}
          <div className="pt-4 border-t space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Show video on:</p>

            <div className="flex items-center justify-between">
              <Label htmlFor="show-pop" className="flex items-center gap-2 text-sm cursor-pointer">
                <Heart className="w-4 h-4 text-pop" />
                Pop Balloon
              </Label>
              <Switch
                id="show-pop"
                checked={showPopBalloon}
                onCheckedChange={(v) => handleVisibilityChange("video_show_pop_balloon", v)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="show-global" className="flex items-center gap-2 text-sm cursor-pointer">
                <Globe className="w-4 h-4 text-blue-500" />
                Global Connect
              </Label>
              <Switch
                id="show-global"
                checked={showGlobalConnect}
                onCheckedChange={(v) => handleVisibilityChange("video_show_global_connect", v)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="show-exhibition" className="flex items-center gap-2 text-sm cursor-pointer">
                <Building2 className="w-4 h-4 text-amber-500" />
                Love Exhibition
              </Label>
              <Switch
                id="show-exhibition"
                checked={showExhibition}
                onCheckedChange={(v) => handleVisibilityChange("video_show_exhibition", v)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Video?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove your intro video. You can always upload a new one later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cover frame picker dialog */}
      <AlertDialog open={showCoverPicker} onOpenChange={setShowCoverPicker}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Choose Cover Frame</AlertDialogTitle>
            <AlertDialogDescription>
              Pause the video at the frame you want to use as the cover image.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
            <video
              src={profile?.video_url || ""}
              className="w-full h-full object-cover"
              controls
              playsInline
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSetCoverFrame}>
              Save Cover Frame
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProfileVideoSection;